import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { useCombobox } from 'downshift';
import {
  Button,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Row,
} from 'react-bootstrap';
import { selectCurrentOrganizationId } from '../../../../modules/auth.js';
import {
  retrieveExternalEntities,
  selectExternalEntities,
  selectRetrieveExternalEntitiesState,
} from '../../../../modules/externalEntities.js';
import { CloseIcon } from '../../../icons/CloseIcon.jsx';

const mapStateToProps = (state) => ({
  organizationId: selectCurrentOrganizationId(state),
  externalEntities: selectExternalEntities(state),
  retrieveSuccess: selectRetrieveExternalEntitiesState(state).success,
});

const mapDispatchToProps = { retrieveExternalEntities };

const ExternalEntitiesSelect = (props) => {
  const {
    retrieveSuccess,
    organizationId,
    retrieveExternalEntities,
    entities,
    updateEntities,
  } = props;

  const handleUnselect = (entity) =>
    updateEntities(entities.filter((item) => item.name !== entity.name));

  const handleRemove = (entity) =>
    window.confirm(
      'Are you sure you want to detach this external group/people?'
    ) && handleUnselect(entity);

  const handleSelect = (entity) => {
    const newExternalEntity = {
      name: entity.name,
    };

    entities.find((item) => item.name === entity.name)
      ? handleUnselect(entity)
      : updateEntities([...entities, newExternalEntity]);
  };

  const renderEntity = (entity) => {
    return (
      <div key={entity.name} className="flex m-t-4 m-l-4 item-style">
        {entity.name}
        <span
          className="btn btn-clear close-btn"
          onClick={() => handleRemove(entity)}
        >
          <CloseIcon />
        </span>
      </div>
    );
  };

  useEffect(() => {
    !retrieveSuccess && retrieveExternalEntities(organizationId);
  }, []);

  return (
    retrieveSuccess && (
      <FormGroup>
        <ControlLabel>
          External Groups or People
          <strong className="bordered bordered-xs text-muted m-l-8">
            Total:&nbsp;
            <span className="text-info-brand count">{entities.length}</span>
          </strong>
        </ControlLabel>
        <div className="m-b-32">
          <div className="bulk-select m-b-12">
            <div className="flex flex-align-start flex-wrap">
              {entities.map(renderEntity)}
            </div>
            <ExternalEntitiesCombobox {...props} handleSelect={handleSelect} />
          </div>
        </div>
      </FormGroup>
    )
  );
};

const ExternalEntitiesCombobox = (props) => {
  const { externalEntities, entities, handleSelect } = props;

  const entitiesNames = new Set(externalEntities.map((i) => i.name));
  const mergedEntities = [
    ...externalEntities,
    ...entities.filter((i) => !entitiesNames.has(i.name)),
  ];

  const [inputItems, setInputItems] = useState(mergedEntities);
  const [newItemName, setNewItemName] = useState('');

  const disabled =
    newItemName.length > 0 &&
    !inputItems.find((item) =>
      item.name.toLowerCase().includes(newItemName.toLowerCase())
    );

  const isActive = (name) => entities.find((item) => item.name === name);

  const addNewEntity = () => {
    handleSelect({ name: newItemName });
    setNewItemName('');
  };

  useEffect(() => {
    setInputItems(mergedEntities);
  }, [entities]);

  const {
    getComboboxProps,
    getInputProps,
    getItemProps,
    getMenuProps,
    getToggleButtonProps,
    isOpen,
  } = useCombobox({
    items: inputItems,
    itemToString: (item) => (item ? item.name : ''),
    onInputValueChange: ({ inputValue }) => {
      if (inputValue) {
        setNewItemName(inputValue);
        setInputItems(
          mergedEntities.filter(({ name }) =>
            name.toLowerCase().includes(inputValue.toLowerCase())
          )
        );
      } else {
        setNewItemName(inputValue);
        setInputItems(mergedEntities);
      }
    },
    onSelectedItemChange: ({ selectedItem }) => handleSelect(selectedItem),
    selectedItem: null,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
            highlightedIndex: state.highlightedIndex,
            inputValue: '', // Don't add the item string as input value at selection
          };
        default:
          return changes;
      }
    },
  });

  return (
    <Row>
      <Col xs={12}>
        <div className={classNames('dropdown', { open: isOpen })}>
          <div className="input-group" {...getComboboxProps()}>
            <FormControl
              className="clear"
              value={newItemName}
              placeholder="Enter Team or Person Outside the Organization"
              {...getInputProps({
                id: 'externalEntities',
                refKey: 'inputRef',
                value: newItemName,
              })}
            />
            <span className="input-group-btn">
              <div className="btn-group btn-group-info">
                <div
                  className="dropdown-toggle btn btn-info btn-clear"
                  role="button"
                  {...getToggleButtonProps()}
                >
                  <span className="caret" />
                </div>
              </div>
            </span>
          </div>
          <ul className="dropdown-menu" {...getMenuProps()}>
            {inputItems.map((entity, index) => {
              if (!entity.name) {
                return (
                  <React.Fragment key="all">
                    <li className="divider" role="separator" />
                  </React.Fragment>
                );
              }
              return (
                <ComboboxItem
                  active={isActive(entity.name)}
                  getItemProps={getItemProps}
                  item={entity}
                  index={index}
                  key={entity.name}
                >
                  {entity.name}
                  &nbsp;
                  {entity.archived ? '(archived)' : ''}
                </ComboboxItem>
              );
            })}
          </ul>
        </div>
      </Col>
      {disabled && (
        <Button
          bsStyle="success"
          onClick={addNewEntity}
          style={{ position: 'absolute' }}
        >
          Add
        </Button>
      )}
    </Row>
  );
};

const ComboboxItem = ({
  active,
  children,
  getItemProps,
  item,
  index,
  ...props
}) => (
  <li className={classNames({ active })} {...props}>
    <a
      href="#"
      {...getItemProps({
        item,
        index,
        onClick: (event) => event.preventDefault(),
      })}
    >
      {children}
    </a>
  </li>
);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ExternalEntitiesSelect);

ExternalEntitiesSelect.propTypes = {
  externalEntities: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      note: PropTypes.string,
    })
  ),
};

ExternalEntitiesCombobox.propTypes = {};
